// Guided branching question flows for the problem-first path selector.
// Presentation lives in PathSelectorModal; this file holds only the logic/data.
// Each question: { id, type: "single"|"multi"|"text", title, help?, summaryLabel,
//   options?: [{value,label,desc?}], showIf?(answers), optional?, placeholder? }

export const QUESTION_FLOWS = {
  employer: [
    {
      id: "need",
      type: "single",
      title: "What do you need help with?",
      summaryLabel: "Primary need",
      options: [
        { value: "hire", label: "Hire for open roles", desc: "Recruitment, sourcing or RPO" },
        { value: "hr", label: "Set up or improve HR", desc: "Consulting, planning, strategy" },
        { value: "digital", label: "Build a website or digital presence" },
        { value: "unsure", label: "Not sure yet", desc: "Help me figure it out" },
      ],
    },
    {
      id: "roles",
      type: "multi",
      title: "Which kinds of roles are you hiring for?",
      summaryLabel: "Roles",
      showIf: (a) => a.need === "hire",
      options: [
        { value: "tech", label: "Technology & Engineering" },
        { value: "sales", label: "Sales & Marketing" },
        { value: "ops", label: "Operations & Support" },
        { value: "leadership", label: "Leadership & Executive" },
        { value: "other", label: "Other" },
      ],
    },
    {
      id: "volume",
      type: "single",
      title: "How much hiring are we talking about?",
      summaryLabel: "Hiring volume",
      showIf: (a) => a.need === "hire",
      options: [
        { value: "1", label: "A single key role" },
        { value: "2-5", label: "2–5 roles" },
        { value: "6+", label: "6 or more roles" },
        { value: "ongoing", label: "An ongoing pipeline" },
      ],
    },
    {
      id: "hrFocus",
      type: "multi",
      title: "Which HR areas matter most?",
      summaryLabel: "HR focus",
      showIf: (a) => a.need === "hr",
      options: [
        { value: "policies", label: "Policies & compliance" },
        { value: "planning", label: "Workforce planning" },
        { value: "talent", label: "Talent strategy" },
        { value: "performance", label: "Performance & culture" },
      ],
    },
    {
      id: "site",
      type: "single",
      title: "What kind of website do you have in mind?",
      summaryLabel: "Website",
      showIf: (a) => a.need === "digital",
      options: [
        { value: "new", label: "A new business website" },
        { value: "redesign", label: "A redesign of an existing site" },
        { value: "ai", label: "An AI-powered website" },
      ],
    },
    {
      id: "timeline",
      type: "single",
      title: "What's your timeline?",
      summaryLabel: "Timeline",
      options: [
        { value: "now", label: "As soon as possible" },
        { value: "quarter", label: "This quarter" },
        { value: "exploring", label: "Just exploring for now" },
      ],
    },
  ],

  candidate: [
    {
      id: "goal",
      type: "single",
      title: "What's your main career goal right now?",
      summaryLabel: "Goal",
      options: [
        { value: "new-role", label: "Land a new role" },
        { value: "switch", label: "Switch industries or functions" },
        { value: "promotion", label: "Grow or get promoted where I am" },
        { value: "explore", label: "Explore my options" },
      ],
    },
    {
      id: "support",
      type: "multi",
      title: "Where do you want support?",
      summaryLabel: "Support needed",
      help: "Pick everything that applies.",
      options: [
        { value: "resume", label: "Resume & ATS" },
        { value: "linkedin", label: "LinkedIn optimisation" },
        { value: "interview", label: "Interview preparation" },
        { value: "consultation", label: "Career consultation" },
        { value: "branding", label: "Professional branding" },
        { value: "strategy", label: "Job search strategy" },
        { value: "cover", label: "Cover letters & applications" },
      ],
    },
    {
      id: "level",
      type: "single",
      title: "Where are you in your career?",
      summaryLabel: "Experience",
      options: [
        { value: "entry", label: "Student / Entry level" },
        { value: "mid", label: "Mid level" },
        { value: "senior", label: "Senior" },
        { value: "exec", label: "Executive / Leadership" },
      ],
    },
    {
      id: "timeline",
      type: "single",
      title: "How soon do you need this?",
      summaryLabel: "Timeline",
      options: [
        { value: "active", label: "I'm actively applying" },
        { value: "soon", label: "In the next few months" },
        { value: "exploring", label: "Just exploring" },
      ],
    },
  ],

  freelancer: [
    {
      id: "focus",
      type: "single",
      title: "What are you focused on?",
      summaryLabel: "Focus",
      options: [
        { value: "clients", label: "Winning more clients" },
        { value: "presence", label: "Building my online presence" },
        { value: "positioning", label: "Sharpening my positioning" },
        { value: "process", label: "Process, tools & systems" },
      ],
    },
    {
      id: "presence",
      type: "multi",
      title: "What would strengthen your presence?",
      summaryLabel: "Presence needs",
      showIf: (a) => a.focus === "clients" || a.focus === "presence",
      options: [
        { value: "website", label: "A personal / portfolio website" },
        { value: "branding", label: "Personal branding" },
        { value: "linkedin", label: "LinkedIn optimisation" },
        { value: "ai-site", label: "An AI-powered site" },
      ],
    },
    {
      id: "stage",
      type: "single",
      title: "Where are you in your journey?",
      summaryLabel: "Stage",
      options: [
        { value: "starting", label: "Just starting out" },
        { value: "scaling", label: "Established and scaling" },
        { value: "pivoting", label: "Pivoting or repositioning" },
      ],
    },
    {
      id: "timeline",
      type: "single",
      title: "What's your timeline?",
      summaryLabel: "Timeline",
      options: [
        { value: "now", label: "Right now" },
        { value: "soon", label: "Soon" },
        { value: "exploring", label: "Just exploring" },
      ],
    },
  ],
};
